import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../auth';
import { useLanguage } from '../i18n';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, ListOrdered, Repeat, FileText, Wallet, Plus, AlertCircle } from 'lucide-react';

export default function ClientDashboard() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [outstanding, setOutstanding] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.company_id) fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/orders?company_id=${user.company_id}`);
      setOrders(res.data);
      const owed = res.data
        .filter(o => o.payment_status === 'unpaid' || o.payment_status === 'partially_paid')
        .reduce((sum, o) => sum + (o.total_amount || 0), 0);
      setOutstanding(owed);
    } catch (err) {
      console.error(err);
    }
  };

  const formatCurrency = (val) => `₩${Number(val).toLocaleString('ko-KR')}`;

  const statusClasses = {
    new: 'status-new',
    in_production: 'status-in-production',
    packed: 'status-packed',
    shipped: 'status-shipped',
    delivered: 'status-delivered',
    cancelled: 'status-cancelled',
  };

  return (
    <div className="space-y-10" data-testid="client-dashboard">
      <div>
        <p className="eyebrow mb-3">{t('portalTitle')}</p>
        <h1 className="h-display text-4xl md:text-5xl">{t('welcome')}, <span className="text-[#556B2F]">{user?.name}</span></h1>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <button
          onClick={() => navigate('/client/new-order')}
          data-testid="action-new-order"
          className="rounded-xl p-7 cff-hover-lift cursor-pointer text-left bg-[#1A1A1A] text-[#F6F4EE] border-0 group relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />
          <Plus className="h-8 w-8 mb-4 text-[#C9A352]" />
          <h3 className="text-xl font-bold mb-1">{t('newOrder')}</h3>
          <p className="text-sm text-[#F6F4EE]/60">LAVASH · VELORA</p>
        </button>

        <button
          onClick={() => navigate('/client/repeat')}
          data-testid="action-repeat-order"
          className="rounded-xl p-7 cff-hover-lift cursor-pointer text-left bg-[#F2EFE6] border border-[#A4B18E]/30"
        >
          <Repeat className="h-8 w-8 mb-4 text-[#556B2F]" />
          <h3 className="text-xl font-bold text-[#1A1A1A] mb-1">{t('repeatOrder')}</h3>
          <p className="text-sm text-[#1A1A1A]/50">Repeat last order in one tap</p>
        </button>

        <button
          onClick={() => navigate('/client/orders')}
          data-testid="action-order-history"
          className="rounded-xl p-7 cff-hover-lift cursor-pointer text-left bg-[#F6E9E5] border border-[#E8C5C8]"
        >
          <ListOrdered className="h-8 w-8 mb-4 text-[#8E2A4E]" />
          <h3 className="text-xl font-bold text-[#1A1A1A] mb-1">{t('orderHistory')}</h3>
          <p className="text-sm text-[#1A1A1A]/50">View all your orders</p>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-6 bg-[#F2EFE6]/60 border-[#A4B18E]/30">
          <p className="eyebrow text-[#556B2F] mb-3">{t('orders')}</p>
          <p className="text-4xl font-extrabold text-[#1A1A1A] cff-text-numeric">{orders.length}</p>
        </Card>
        <Card className="p-6 bg-[#F6E9E5]/60 border-[#E8C5C8]">
          <p className="eyebrow text-[#8E2A4E] mb-3">{t('outstandingBalance')}</p>
          <p className="text-3xl font-extrabold text-[#1A1A1A] cff-text-numeric">{formatCurrency(outstanding)}</p>
        </Card>
      </div>

      {/* Recent Orders */}
      <Card className="p-6 md:p-8 bg-card border-[#1A1A1A]/8">
        <h2 className="text-xl font-bold text-[#1A1A1A] mb-6">{t('orderHistory')}</h2>
        {orders.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingCart className="h-12 w-12 text-[#1A1A1A]/15 mx-auto mb-4" />
            <p className="text-[#1A1A1A]/50 mb-6">No orders yet</p>
            <Button onClick={() => navigate('/client/new-order')} className="cff-button-primary px-6" data-testid="first-order-btn">
              Create your first order
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1A1A1A]/10 text-left text-[10px] uppercase tracking-widest text-[#1A1A1A]/50">
                  <th className="pb-3 font-semibold">{t('orderNumber')}</th>
                  <th className="pb-3 font-semibold">{t('plannedShipmentDate')}</th>
                  <th className="pb-3 font-semibold text-right">{t('totalAmount')}</th>
                  <th className="pb-3 font-semibold">{t('status')}</th>
                </tr>
              </thead>
              <tbody>
                {orders.slice(0, 10).map(order => (
                  <tr key={order.id} className="border-b border-[#1A1A1A]/5 hover:bg-[#F2EFE6]/30 cursor-pointer transition-colors" onClick={() => navigate(`/client/orders`)} data-testid={`client-order-${order.order_number}`}>
                    <td className="py-3 font-mono text-xs">{order.order_number}</td>
                    <td className="py-3 text-[#1A1A1A]/60 cff-text-numeric">
                      {order.planned_shipment_date && new Date(order.planned_shipment_date).toLocaleDateString()}
                    </td>
                    <td className="py-3 text-right cff-text-numeric font-bold">{formatCurrency(order.total_amount)}</td>
                    <td className="py-3">
                      <span className={`status-badge ${statusClasses[order.status]}`}>{order.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
